import React, {useEffect, useMemo, useState} from "react";
import {EnvironmentAndOrganisations} from "../types/EnvironmentAndOrganisations";
import {OrganisationDTO} from "../types/OrganisationDTO";
import OrganisationService from "../services/OrganisationService";
import {
    Alert,
    AppHeader,
    DataList,
    ListItemElement,
    RoundButton,
    Searchbox,
    Spinner
} from "@tag/tag-components-react-v4";
import styles from './OrganisationList.module.scss';
import {
    ArrowRightFilled, CircleInfoLined,
    Database2Filled,
    FilingCabinetFilled,
    IDLined,
    MenuFilled, SearchFilled,
    WeatherCloudLined
} from "@tag/tag-icons";
import HelperMethods from "../../General/services/HelperMethods";
import OrganisationInfoModal from "./OrganisationInfoModal/OrganisationInfoModal";
import CustomerMenuModal from "./CustomerMenuModal/CustomerMenuModal";
import { useSearchParams } from 'react-router-dom';
import {Organisation} from "../types/Organisation";
import { ProjectAccents } from "../../General/config/projectAccents";


function OrganisationList() {
    
    const [searchParams] = useSearchParams();
    const environmentName = searchParams.get('environmentName');
    const organisationId = searchParams.get('organisationId');
    const [hasProcessedParams, setHasProcessedParams] = useState(false);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [allEnvironmentAndOrganisations, setAllEnvironmentAndOrganisations] = useState<EnvironmentAndOrganisations[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [alert, setAlert] = useState<{type: any, message: string}>();
    const [selectedOrganisationForInfo, setSelectedOrganisationForInfo] = useState<OrganisationDTO|undefined>();
    const [selectedCustomerForMenu, setSelectedCustomerForMenu] = useState<OrganisationDTO|undefined>();

    useEffect(() => {
        loadOrganisationData();
    }, []);

    // Process query params once after data loads
    useEffect(() => {
        if (!loading && !hasProcessedParams && allEnvironmentAndOrganisations.length > 0) {
            if (environmentName && organisationId) {
                const matchedOrg = allEnvironmentAndOrganisations
                    .find(e => e.environmentName === environmentName)
                    ?.organisations.find(o => o.id === organisationId);

                if (matchedOrg) {
                    setSearchTerm(organisationId)
                    setSelectedCustomerForMenu(getDTOFromOrganisationEntity(matchedOrg, environmentName));
                } else {
                    setAlert({
                        type: 'warning',
                        message: `Could not find organisation ${organisationId} in ${environmentName}`
                    });
                }
            }
            setHasProcessedParams(true);
        }
    }, [loading, hasProcessedParams, allEnvironmentAndOrganisations, environmentName, organisationId]);
    
    const getDTOFromOrganisationEntity = (organisation: Organisation, environmentName: string): OrganisationDTO => {
        return {
            ...organisation, // Spread the properties of the Organisation object
            environmentName: environmentName,
            image: undefined,
            initials: organisation.trimmedWorkspaceOrganisationName ? HelperMethods.getInitials(organisation.trimmedWorkspaceOrganisationName) : '??',
            displayedName: organisation.trimmedWorkspaceOrganisationName ? organisation.trimmedWorkspaceOrganisationName : 'NULL (No value in DB)'
        }
    }


    const loadOrganisationData = async () => {
        setLoading(true);
        
        const envAndOrgs = await OrganisationService.getAllOrganisations();
        setAllEnvironmentAndOrganisations(envAndOrgs);
        
        setLoading(false);
    }
    
    const getOrganisationData = (): OrganisationDTO[] => {
        if (!searchTerm) {
            return [];
        }
        
        let allOrgs: OrganisationDTO[] = allEnvironmentAndOrganisations.flatMap(eao =>
            eao.organisations.map(organisation => (getDTOFromOrganisationEntity(organisation, eao.environmentName)))
        );
        
        return  HelperMethods.advancedFuseSearch(searchTerm, allOrgs, ['displayedName', 'id']);
    }

    const organisationData = useMemo(
        () => getOrganisationData(), 
        [searchTerm, allEnvironmentAndOrganisations]
    );

    let content;
    if (loading) {
        content = <Spinner/>
    } else {
        content = <>
            <AppHeader
                heading="Organisation List"
                icon={<SearchFilled/>}
                accent={ProjectAccents.PoliciesAndProcedures}
            >
                <div
                    className={'customer-list-search-container'}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Searchbox
                        onSearch={(e) => setSearchTerm(e)}
                        clearButton
                        style={{width: '100%'}}
                        value={searchTerm}
                    />
                </div>
            </AppHeader>
            <DataList
                avatarProps={{srcField: 'image', placeholderField: 'initials', accent: ProjectAccents.PoliciesAndProcedures}}
                primaryField="displayedName"
                renderRightElements={(dataItem) => (
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                        <RoundButton
                            icon={<CircleInfoLined />}
                            accent={ProjectAccents.PoliciesAndProcedures}
                            onClick={() => setSelectedOrganisationForInfo(dataItem)}
                        />
                        <RoundButton
                            icon={<MenuFilled />}
                            accent={ProjectAccents.PoliciesAndProcedures}
                            onClick={() => setSelectedCustomerForMenu(dataItem)}
                        />
                    </div>
                )}
                renderSubItemElements={(dataItem) => (
                    <>
                        <ListItemElement
                            icon={<FilingCabinetFilled accent={ProjectAccents.PoliciesAndProcedures}/> }
                        >
                            {dataItem.environmentName}
                        </ListItemElement>
                        <ListItemElement
                            icon={<IDLined accent={ProjectAccents.PoliciesAndProcedures}/>}
                            style={{marginLeft: '2em'}}
                        >
                            {dataItem.id}
                        </ListItemElement>
                    </>
                )}
                data={organisationData}
            />
            {alert ? 
                <Alert
                    className={styles.OrganisationListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={2400}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }
        </>
    }

    return (
        <>
            {content}
            {selectedCustomerForMenu ?
                <CustomerMenuModal
                    selectedOrganisation={selectedCustomerForMenu}
                    handleClose={() => {
                        setSelectedCustomerForMenu(undefined)
                    }}
                    reloadCustomerData={loadOrganisationData}
                    setAlert={setAlert}
                /> : undefined
            }
            {selectedOrganisationForInfo ?
                <OrganisationInfoModal
                    selectedOrganisation={selectedOrganisationForInfo}
                    handleClose={() => {
                        setSelectedOrganisationForInfo(undefined)
                    }}
                /> : undefined
            }
        </>
    )

}

export default OrganisationList;
